import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import PrinterButton from "../../pages/Indent/IndentComponents/PrinterButton";
import ExportButton from "../../pages/Indent/IndentComponents/ExportButton";
import { IndentPDF } from "../Header/IndentPDF";

const DynamicTable = ({
  columns,
  data,
  selectedRow,
  onRowSelect,
  onDoubleClick,
  rowKey = "id",
  userIdKey = "userId",
  noDataMessage = "No data available",
  highlightColor = "bg-blue-100",
  hoverColor = "hover:bg-gray-50",
  userRole,
  mobileView = false,
}) => {
  console.log("DynamicTable render", Date.now());
  const [clickTimeout, setClickTimeout] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [pdfError, setPdfError] = useState("");
  const rowsPerPage = 10;
  const userId = useSelector((state) => state.auth.id);

  // Debug log for incoming props
  useEffect(() => {
    console.log("DynamicTable props:", {
      columns,
      dataLength: data?.length,
      userRole,
      userId
    });
  }, [columns, data, userRole, userId]);

  // Modified filtering logic to handle Store Officer role correctly
  const displayData = userRole?.toLowerCase() === "admin"
    ? data
    : userRole?.toLowerCase() === "store officer"
      ? data // Remove the filter since the API already filters by SOID
      : data;

  const totalPages = Math.ceil(displayData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = displayData.slice(startIndex, startIndex + rowsPerPage);

  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const handleRowClick = (row) => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    if (onDoubleClick) {
      onDoubleClick(row);
      }
    } else {
      const timeoutId = setTimeout(() => {
        if (onRowSelect) {
          onRowSelect(row);
        }
        setClickTimeout(null);
      }, 200);
      setClickTimeout(timeoutId);
    }
  };

  const getRowBackgroundColor = (row) => {
    if (!row) return "";
    
    if (row.Status?.toLowerCase() === "closed" || 
        row.Status?.toLowerCase() === "force closed") {
      return "bg-green-100";
    }

    if (row.L1 === "Approved" &&
        row.L2 === "Approved" &&
        row.PO === "Approved" &&
        row.PH === "Approved" &&
        row.IR === "Approved") {
      return "bg-green-100";
    }

    return selectedRow?.[rowKey] === row[rowKey] ? highlightColor : "";
  };

  const fetchIndentData = async (indent) => {
    // First fetch the indent details
    const indentResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/indent/indents/${indent.IndentID}`
    );
    const indentData = await indentResponse.json();
    const indentDetails = Array.isArray(indentData)
      ? indentData[0]
      : indentData;

    // Then fetch the items
    const itemsResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/indent/indentitems/${indent.IndentID}`
    );
    const itemsData = await itemsResponse.json();

    // Finally fetch the status
    const statusResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/indent/status/${indent.IndentID}`
    );
    const statusData = await statusResponse.json();
    const status = Array.isArray(statusData) ? statusData[0] : statusData;

    console.log('Indent details:', indentDetails);
    console.log('Status data:', status);

    return {
      indentId: indent.IndentID,
      creationDate: indentDetails?.CreationDate,
      PEApprovalDate: indentDetails?.PEApprovalDate || indentDetails?.PEApprovedDate || null,
      L1ApprovedDate: indentDetails?.L1ApprovedDate || null,
      L2ApprovalDate: indentDetails?.L2ApprovalDate || null,
      projectName: indentDetails?.ProjectName || "N/A",
      userName: indentDetails?.UserName || "N/A",
      status: indentDetails?.Status || "N/A",
      taskScheduled: indentDetails?.TaskSchedule || "N/A",
      invoicingType: indentDetails?.TypeName || "N/A",
      // Add approval flags from both sources
      PO: status?.PO || indentDetails?.PO || 0,
      L1: status?.L1 || indentDetails?.L1 || 0,
      L2: status?.L2 || indentDetails?.L2 || 0,
      items: itemsData.map((item) => ({
        name: item.ItemName,
        category: item.CategoryName,
        au: item.UnitName,
        quantity: item.Quantity,
        stock: item.Stock,
        description: item.Description || "__",
        promisedDays: item.NormalDays,
        promisedDays2: item.EmergencyDays,
        committedDate: item.CommitedDate,
      })),
    };
  };

  const getPDFComponent = (data) => {
    return <IndentPDF data={data} />;
  };

  const formatValue = (column, value) => {
    if (column.format) {
      return column.format(value);
    }
    
    if (column.key === "CreationDate") {
      return value || "N/A";
    }
    
    return value;
  };

  // Mobile card view
  const MobileRow = ({ row }) => {
    const isExpanded = expandedRows.has(row[rowKey]);

    return (
      <div
        className={clsx(
          "border rounded-lg mb-2 p-3",
          getRowBackgroundColor(row),
          hoverColor
        )}
        onClick={() => handleRowClick(row)}
      >
        <div className="flex justify-between items-center">
          <div className="font-medium">ID: {row.IndentID}</div>
          <div className="flex items-center gap-2">
            <ExportButton
              getPDFComponent={getPDFComponent}
              filename={`indent-${row.IndentID}`}
              fetchData={() => fetchIndentData(row)}
              onError={(error) => setPdfError("Failed to generate export. Please try again.")}
              className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors duration-200"
              iconSize={16}
            />
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleRowExpansion(row[rowKey]);
            }}
            className="p-1 rounded-full hover:bg-gray-200"
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          </div>
        </div>

        <div className="text-sm text-gray-600 mt-1">
          Status: {row.Status || 'N/A'}
        </div>

        {isExpanded && (
          <div className="mt-2 space-y-1 text-sm">
            {columns.map(column => (
              column.key !== "IndentID" && column.key !== "Status" && (
                <div key={column.key} className="flex justify-between">
                  <span className="font-medium">{column.label}:</span>
                  <span>{formatValue(column, row[column.key])}</span>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    );
  };

  // Pagination controls
  const PaginationControls = () => (
    <div className="flex flex-col sm:flex-row items-center justify-between px-2 sm:px-4 py-3 space-y-3 sm:space-y-0">
      <div className="text-sm text-gray-700">
        Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, displayData.length)} of{" "}
        {displayData.length} entries
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className={clsx(
            "inline-flex items-center rounded px-2 sm:px-3 py-1 sm:py-2",
            "border border-gray-300 bg-white text-sm font-medium",
            "focus:outline-none focus:ring-2 focus:ring-blue-500",
            currentPage === 1
              ? "cursor-not-allowed text-gray-400"
              : "text-gray-700 hover:bg-gray-50"
          )}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline ml-1">Previous</span>
        </button>
        <span className="text-sm text-gray-700">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className={clsx(
            "inline-flex items-center rounded px-2 sm:px-3 py-1 sm:py-2",
            "border border-gray-300 bg-white text-sm font-medium",
            "focus:outline-none focus:ring-2 focus:ring-blue-500",
            currentPage === totalPages
              ? "cursor-not-allowed text-gray-400"
              : "text-gray-700 hover:bg-gray-50"
          )}
        >
          <span className="hidden sm:inline mr-1">Next</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  // Return empty div if no data
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        {noDataMessage}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mobile View */}
      {mobileView && (
        <div className="block sm:hidden">
          <div className="space-y-2">
            {paginatedData.map((row) => (
              <MobileRow key={row[rowKey]} row={row} />
            ))}
          </div>
        </div>
      )}
      {/* Desktop View */}
      <div className="hidden sm:block overflow-x-auto">
        {pdfError && (
          <div className="text-red-500 mb-2">{pdfError}</div>
        )}
        
        {mobileView ? (
          <div className="space-y-2">
            {paginatedData.map((row) => (
              <MobileRow key={row[rowKey]} row={row} />
            ))}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column.label}
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedData.map((row) => (
                <tr
                  key={row[rowKey]}
                  onClick={() => handleRowClick(row)}
                  className={clsx(
                    "cursor-pointer transition-colors duration-150",
                    getRowBackgroundColor(row),
                    hoverColor
                  )}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                    >
                      {formatValue(column, row[column.key])}
                    </td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center gap-2">
                      <ExportButton
                        getPDFComponent={getPDFComponent}
                        filename={`indent-${row.IndentID}`}
                        fetchData={() => fetchIndentData(row)}
                        onError={(error) => setPdfError("Failed to generate export. Please try again.")}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors duration-200"
                        iconSize={20}
                      />

                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PaginationControls />
    </div>
  );
};

export default React.memo(DynamicTable);